import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import EntryTypeModal from "@/components/EntryTypeModal";
import BottomNavigation from "@/components/BottomNavigation";
import { ScreenHeader } from "@/components/redesign";
import { useToast } from "@/hooks/use-toast";
import { entriesAPI } from "@/api/entries";
import { peopleAPI } from "@/api/people";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, CheckCircle2, AlertCircle, QrCode } from "lucide-react";
import { uploadFile } from "@/lib/storage-adapter";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { CapacitorBarcodeScanner } from '@capacitor/barcode-scanner';
import { useTranslation } from "react-i18next";
import OpenAccountSettleDialog from "@/components/OpenAccountSettleDialog";
import type { AdvanceSubMethod } from "@/lib/paymentMethods";
import { getCurrencySymbol } from "@/utils/currencyUtils";

// Style for hiding background during QR scan
const scannerStyles = `
.scanner-active {
  visibility: hidden !important;
  opacity: 0 !important;
  position: fixed !important;
  width: 100% !important;
  height: 100% !important;
  top: 0 !important;
  left: 0 !important;
  z-index: -1 !important;
}

.scanner-container {
  position: fixed !important;
  width: 100% !important;
  height: 100% !important;
  top: 0 !important;
  left: 0 !important;
  z-index: 9999 !important;
  background: transparent !important;
}
`;

const EntryDetailPage: React.FC = () => {
  const { entryId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [entryData, setEntryData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [isScanning, setIsScanning] = useState(false);
  const [settleDialogOpen, setSettleDialogOpen] = useState(false);
  const [outstanding, setOutstanding] = useState(0);
  
  // State for upload progress
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Function to handle QR code scanning to link person
  const handleQRScan = async () => {
    if (!entryId) {
              toast({
          title: t('common.error'),
          description: t('entries.entry_not_found'),
          variant: "destructive"
        });
      return;
    }

    try {
      // Hide the background and set scanning state
      setIsScanning(true);
      document.querySelector('body')?.classList.add('scanner-active');
      
      
      // Start the scan
      const result = await CapacitorBarcodeScanner.scanBarcode({
        scanInstructions: "Kodu taramak için kamerayı barkoda doğrultun"
      });
      
      // Scanning completed
      setIsScanning(false);
      document.querySelector('body')?.classList.remove('scanner-active');
      
      
      
      if (result.hasContent) {
        // Log the scanned content (personId)
        const scannedPersonId = result.content;
        
        
        try {
          // Use the new API function to link the person to the entry
          const success = await peopleAPI.linkPersonToEntry(entryId, scannedPersonId);
          
          if (success) {
            toast({
              title: "Başarılı",
              description: "Kişi kaydı başarıyla ilişkilendirildi.",
            });
            
            // Reload the entry data to reflect changes
            if (entryData) {
              setEntryData({
                ...entryData,
                personId: scannedPersonId
              });
            }
          }
        } catch (linkError: any) {
          
          toast({
            title: "Bağlantı Hatası",
            description: linkError.message || "Kişi ile kayıt ilişkilendirilemedi.",
            variant: "destructive"
          });
        }
      } else {
        toast({
          title: "Hata",
          description: "QR okuma başarısız oldu.",
          variant: "destructive"
        });
      }
    } catch (error) {
      
      toast({
        title: "Hata",
        description: "QR okuma iptal edildi.",
        variant: "destructive"
      });
    } finally {
      // Always ensure we stop scanning and restore UI
      setIsScanning(false);
      document.querySelector('body')?.classList.remove('scanner-active');
    }
  };

  // Add QR scanner styles to head
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = scannerStyles;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Fetch the entry data when component mounts
  useEffect(() => {
    const fetchEntry = async () => {
      if (!entryId) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        const data = await entriesAPI.getEntry(entryId);
        
        // Determine the official status
        const isOfficial = data.is_official || data.has_invoice || false;
        const officialSuffix = isOfficial ? 'official' : 'unofficial';
        
        // Get correct amount based on currency flags
        const originalCurrency = data.original_currency || 'TRY';
        const originalAmount = data.original_amount || 0;
        
        // If there's no original amount, try to get amounts from the currency-specific fields
        let tryAmount = data[`amount_try_${officialSuffix}`] || data.amount || 0;
        let usdAmount = data[`amount_usd_${officialSuffix}`] || data.amount_usd || 0;
        let eurAmount = data[`amount_eur_${officialSuffix}`] || data.amount_eur || 0;
        
        // Decide which amount to show based on original currency
        let displayAmount = originalAmount;
        if (displayAmount === 0) {
          if (originalCurrency === 'USD') {
            displayAmount = usdAmount;
          } else if (originalCurrency === 'EUR') {
            displayAmount = eurAmount;
          } else {
            displayAmount = tryAmount;
          }
        }
        
        
        
        // Format the data for the EntryTypeModal
        setEntryData({
          type: data.type,
          date: new Date(data.date),
          dueDate: data.due_date ? new Date(data.due_date) : undefined,
          projectId: data.project_id,
          personId: data.person_id,
          description: data.description || "",
          amount: displayAmount,
          original_amount: originalAmount,
          original_currency: originalCurrency,
          currency: originalCurrency,
          hasInvoice: isOfficial,
          imageUrl: data.file_url || data.image_url,
          // Payment-method fields (for the open-account settle action)
          paymentMethod: data.payment_method,
          isSettled: data.is_settled === true
          // We don't include the image file as we're using the existing URL
        });
      } catch (err) {
        
        setError("Kayıt yüklenirken bir hata oluştu.");
        toast({
          title: "Hata",
          description: "Kayıt yüklenirken bir hata oluştu.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchEntry();
  }, [entryId, toast]);

  // Use the proper uploadImage function with S3 support
  const uploadImage = async (file: File, path: string): Promise<string> => {
    try {
      // Validate file before uploading
      const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic'];
      const MAX_FILE_SIZE = 4 * 1024 * 1024; // 4MB

      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        throw new Error("Geçersiz dosya formatı. Lütfen geçerli bir resim dosyası seçin (JPEG, PNG, GIF, WEBP)");
      }

      if (file.size > MAX_FILE_SIZE) {
        throw new Error("Dosya çok büyük. Lütfen 4MB'dan küçük bir dosya seçin");
      }

      // Keep modal open during upload
      setIsModalOpen(true);
      setIsUploading(true);
      setUploadProgress(0);
      setUploadStatus('uploading');
      setUploadError(null);
      
      // Simulate initial progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          const newProgress = prev < 92 ? prev + (Math.random() * 3) : prev;
          
          return newProgress;
        });
      }, 200);

      try {
        
        const timestamp = Date.now();
        const uniquePath = `${path}_${timestamp}`;
        
        
        const url = await uploadFile(file, 'project-images', `projects/${uniquePath}`);
        
        
        // Complete the progress bar
        clearInterval(progressInterval);
        setUploadProgress(100);
        setUploadStatus('success');
        
        
        // Short delay to show completion
        
        await new Promise(resolve => setTimeout(resolve, 2000));
        setIsUploading(false);
        
        
        return url;
      } catch (error) {
        // Clear the progress interval on error
        clearInterval(progressInterval);
        
        setUploadStatus('error');
        setUploadError(error instanceof Error ? error.message : 'Resim yüklenirken bir hata oluştu.');
        setIsUploading(false);
        
        // Give time to see the error
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        throw error;
      }
    } catch (error) {
      
      setUploadStatus('error');
      setUploadError(error instanceof Error ? error.message : 'Resim yüklenirken bir hata oluştu.');
      setIsUploading(false);
      throw error;
    }
  };

  const handleEntrySubmit = async (data: any) => {
    
    
    try {
      // Validate required fields
      if (!data.type) {
        throw new Error("Entry type is required");
      }
      if (!data.date) {
        throw new Error("Date is required");
      }
      if (!data.projectId) {
        throw new Error("Project is required");
      }
      
      // Validate amount for expense and labor cost entries
      if ((data.type === 'expense' || data.type === 'laborCost') && 
          (!data.amount || isNaN(parseFloat(data.amount)))) {
        
        throw new Error("Valid amount is required for expenses and labor costs");
      }
      
      // If there is an amount, convert it to a number
      if (data.amount) {
        data.amount = parseFloat(data.amount);
      }
      
      // Upload image if present and is a new file (not a URL)
      let imageUrl = data.imageUrl || '';
      let uploadComplete = false;
      
      if (data.imageFile) {
        
        
        // Keep the modal open
        setIsModalOpen(true);
        
        try {
          imageUrl = await uploadImage(data.imageFile, `${data.projectId}`);
          
          uploadComplete = true;
        } catch (uploadError) {
          
          toast({
            title: "Resim yüklenemedi",
            description: "Lütfen daha sonra tekrar deneyin veya farklı bir resim seçin.",
            variant: "destructive",
          });
          // We'll continue with the previous image URL rather than failing the entire form submission
        }
      }
      
      // Prepare data for API call
      const updateData = {
        type: data.type,
        date: data.date,
        due_date: data.dueDate,
        project_id: data.projectId,
        person_id: data.personId,
        description: data.description || "",
        amount: data.amount,
        image_url: imageUrl,
        hasInvoice: data.hasInvoice
      };
      
      
      
      // Call the API to update the entry
      
      const result = await entriesAPI.updateEntry(entryId!, updateData);
      
      
      // Show success toast
      toast({
        title: "Başarılı",
        description: "Kayıt başarıyla güncellendi.",
      });
      
      // Trigger refresh for person data if necessary
      if (data.personId) {
        try {
          // We don't need to await this
          entriesAPI.triggerPersonDataRefresh(data.personId);
        } catch (error) {
          
        }
      }
      
      // Verify upload completed if there was an image
      if (data.imageFile && !uploadComplete) {
        
      } else if (data.imageFile) {
        
      }
      
      // Close modal and navigate after a brief delay
      
      setTimeout(() => {
        
        setIsModalOpen(false);
        
        // Navigate back to project detail page if we have a project ID
        if (data.projectId) {
          navigate(`/project/${data.projectId}`);
        } else {
          // Otherwise go to home
          navigate("/");
        }
      }, 3000);
      
    } catch (error) {
      
      if (error instanceof Error) {
        
        
        
        // Show more specific error message if available
        toast({
          title: "Hata",
          description: error.message || "Kayıt güncellenirken bir hata oluştu.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Hata",
          description: "Kayıt güncellenirken bir hata oluştu.",
          variant: "destructive",
        });
      }
    }
  };

  // Render the upload status UI
  const renderUploadStatus = () => {
    if (!isUploading && uploadStatus === 'idle') return null;

    return (
      <div className="w-full max-w-3xl mb-4 p-4 bg-white border border-gray-200 rounded-2xl shadow-sm z-50">
        <div className="space-y-4">
          <h3 className="font-semibold text-center text-gray-900">{t('uploads.file_upload_status')}</h3>

          {uploadStatus === 'uploading' && (
            <>
              <div className="flex items-center text-sm text-gray-500">
                <Loader2 className="h-5 w-5 mr-2 animate-spin text-brand-600" />
                <span className="text-base">{t('uploads.uploading_file', { progress: Math.round(uploadProgress) })}</span>
              </div>
              <Progress value={uploadProgress} className="w-full h-2" />
              <p className="text-xs text-center text-gray-400">{t('uploads.wait_until_page_close')}</p>
            </>
          )}

          {uploadStatus === 'success' && (
            <div className="flex items-center justify-center text-success-600 py-2">
              <CheckCircle2 className="h-6 w-6 mr-2" />
              <span className="text-base font-medium">{t('uploads.file_uploaded_successfully')}</span>
            </div>
          )}

          {uploadStatus === 'error' && (
            <div className="flex items-center justify-center text-error-600 py-2">
              <AlertCircle className="h-6 w-6 mr-2" />
              <span className="text-base font-medium">{uploadError || t('uploads.file_upload_error')}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Open the settle dialog for an open-account entry (fetch remaining first)
  const openSettleDialog = async () => {
    if (!entryId) return;
    try {
      const remaining = await entriesAPI.getOpenAccountOutstanding(entryId);
      setOutstanding(remaining);
    } catch {
      setOutstanding(entryData?.original_amount || 0);
    }
    setSettleDialogOpen(true);
  };

  // Record a (partial or full) payment against an open-account (Açık Hesap) entry
  const handleOpenAccountPayment = async (amount: number, method: AdvanceSubMethod, dueDate?: Date) => {
    if (!entryId) return;
    try {
      await entriesAPI.addOpenAccountPayment(entryId, amount, method, dueDate, user?.id);
      toast({
        title: "Başarılı",
        description:
          method === "check"
            ? "Çek ile ödeme kaydedildi. Çek ödendiğinde maliyete eklenecek."
            : "Ödeme kaydedildi.",
      });
      setTimeout(() => {
        if (entryData?.projectId) {
          navigate(`/project/${entryData.projectId}`);
        } else {
          navigate("/");
        }
      }, 1500);
    } catch (e: any) {
      toast({
        title: "Hata",
        description: e?.message || "İşlem başarısız oldu.",
        variant: "destructive",
      });
      throw e;
    }
  };

  // Render the "pay open account" button for unsettled open-account entries
  const renderSettleButton = () => {
    if (!entryData || entryData.paymentMethod !== "open_account" || entryData.isSettled) {
      return null;
    }
    return (
      <div className="mt-4 mb-2">
        <Button
          onClick={openSettleDialog}
          className="w-full bg-amber-600 hover:bg-amber-700 text-white rounded-md"
        >
          Açık Hesabı Öde
        </Button>
      </div>
    );
  };

  // Function to render QR link button
  const renderQRLinkButton = () => {
    if (!entryId || !entryData) return null;

    return (
      <div className="mt-4 mb-2">
        <Button
          onClick={handleQRScan}
          className="w-full bg-success-600 hover:bg-success-700 text-white rounded-md"
        >
          <QrCode className="h-4 w-4 mr-2" /> QR ile Kişi Bağla
        </Button>
      </div>
    );
  };

  return (
    <div className="page-container bg-gray-25">
      <ScreenHeader title={t('entries.edit_entry')} />

      <div className="main-content bg-gray-25 flex items-center justify-center flex-col px-4 pt-4">
        {renderUploadStatus()}

        <div className="w-full max-w-3xl">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center p-8">
              <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
              <p className="mt-2 text-gray-500">{t('entries.loading_entry')}</p>
            </div>
          ) : error ? (
            <div className="bg-white border border-error-600/20 rounded-2xl p-8 text-center shadow-sm">
              <p className="text-error-600 mb-4">{error}</p>
              <button
                onClick={() => navigate(-1)}
                className="px-4 py-2 bg-brand-600 text-white rounded-md font-medium"
              >
                {t('common.back')}
              </button>
            </div>
          ) : entryData ? (
            <div className="mb-20">
              {renderSettleButton()}
              {renderQRLinkButton()}
              <EntryTypeModal
                isOpen={isModalOpen}
                onClose={() => navigate(-1)}
                onSubmit={handleEntrySubmit}
                initialData={entryData}
              />
            </div>
          ) : (
            <div className="bg-white border border-error-600/20 rounded-2xl p-8 text-center shadow-sm">
              <p className="text-error-600 mb-4">{t('entries.entry_not_found')}</p>
              <button
                onClick={() => navigate(-1)}
                className="px-4 py-2 bg-brand-600 text-white rounded-md font-medium"
              >
                {t('common.back')}
              </button>
            </div>
          )}
        </div>
      </div>

      <OpenAccountSettleDialog
        open={settleDialogOpen}
        onOpenChange={setSettleDialogOpen}
        outstanding={outstanding}
        currencySymbol={getCurrencySymbol(entryData?.original_currency || "TRY")}
        onPay={handleOpenAccountPayment}
      />

      <BottomNavigation />
    </div>
  );
};

export default EntryDetailPage;